import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  Lock,
  Menu,
  Dashboard as DashboardIcon,
  People,
  BookOnline,
  ContactMail,
  Logout,
} from '@mui/icons-material';
import {
  ADMIN_PANEL_EMAIL,
  ADMIN_PANEL_PASSWORD,
  ADMIN_SESSION_KEY,
  ADMIN_SESSION_EMAIL_KEY,
  ADMIN_RPC_SESSION_PASSWORD_KEY,
} from '../../lib/adminCredentials';
import { fetchAppUsersForAdmin } from '../../lib/adminUsersApi';
import { fetchBookingsForAdmin } from '../../lib/adminBookingsApi';
import { fetchContactMessagesForAdmin } from '../../lib/adminContactMessagesApi';
import './Admin.css';

const normalizeEmail = (value) => value.trim().toLowerCase();

const isAdminSession = () =>
  typeof window !== 'undefined' && sessionStorage.getItem(ADMIN_SESSION_KEY) === '1';

const getSessionAdminEmail = () => {
  if (typeof window === 'undefined') return '';
  return sessionStorage.getItem(ADMIN_SESSION_EMAIL_KEY) || '';
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
};

const formatDateOnly = (value) => {
  if (!value) return '—';
  const str = typeof value === 'string' ? value.slice(0, 10) : String(value).slice(0, 10);
  try {
    return new Date(`${str}T12:00:00`).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '—';
  }
};

const formatMoney = (amount, currency) => {
  if (amount == null || amount === '') return '—';
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  const cur = (currency || 'GBP').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);
  } catch {
    return `${cur} ${n}`;
  }
};

const guestLabel = (b) => {
  const name = [b.guestFirstName, b.guestLastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (b.guestEmail) return b.guestEmail;
  return '—';
};

const truncateText = (value, maxLen = 140) => {
  if (value == null || value === '') return '—';
  const s = String(value).trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
};

const ADMIN_NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', Icon: DashboardIcon },
  { id: 'users', label: 'Users', Icon: People },
  { id: 'bookings', label: 'Bookings', Icon: BookOnline },
  { id: 'contact', label: 'Contact', Icon: ContactMail },
];

const AdminDashboard = ({ adminEmail, onLogout }) => {
  const displayEmail = adminEmail || ADMIN_PANEL_EMAIL;
  const [section, setSection] = useState('dashboard');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState('');
  const [contactMessages, setContactMessages] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');

  const loadUsers = useCallback(async () => {
    setUsersError('');
    const pw =
      typeof window !== 'undefined'
        ? sessionStorage.getItem(ADMIN_RPC_SESSION_PASSWORD_KEY)
        : '';
    if (!adminEmail || !pw) {
      setUsersError('Sign out and sign in again to load this list.');
      setUsers([]);
      return;
    }
    setUsersLoading(true);
    try {
      const rows = await fetchAppUsersForAdmin(adminEmail, pw);
      setUsers(rows);
    } catch (e) {
      setUsers([]);
      setUsersError(e?.message || 'Could not load users.');
    } finally {
      setUsersLoading(false);
    }
  }, [adminEmail]);

  const loadBookings = useCallback(async () => {
    setBookingsError('');
    const pw =
      typeof window !== 'undefined'
        ? sessionStorage.getItem(ADMIN_RPC_SESSION_PASSWORD_KEY)
        : '';
    if (!adminEmail || !pw) {
      setBookingsError('Sign out and sign in again to load this list.');
      setBookings([]);
      return;
    }
    setBookingsLoading(true);
    try {
      const rows = await fetchBookingsForAdmin(adminEmail, pw);
      setBookings(rows);
    } catch (e) {
      setBookings([]);
      setBookingsError(e?.message || 'Could not load bookings.');
    } finally {
      setBookingsLoading(false);
    }
  }, [adminEmail]);

  const loadContactMessages = useCallback(async () => {
    setContactError('');
    const pw =
      typeof window !== 'undefined'
        ? sessionStorage.getItem(ADMIN_RPC_SESSION_PASSWORD_KEY)
        : '';
    if (!adminEmail || !pw) {
      setContactError('Sign out and sign in again to load this list.');
      setContactMessages([]);
      return;
    }
    setContactLoading(true);
    try {
      const rows = await fetchContactMessagesForAdmin(adminEmail, pw);
      setContactMessages(rows);
    } catch (e) {
      setContactMessages([]);
      setContactError(e?.message || 'Could not load contact messages.');
    } finally {
      setContactLoading(false);
    }
  }, [adminEmail]);

  useEffect(() => {
    if (section !== 'users') return;
    loadUsers();
  }, [section, loadUsers]);

  useEffect(() => {
    if (section !== 'bookings') return;
    loadBookings();
  }, [section, loadBookings]);

  useEffect(() => {
    if (section !== 'contact') return;
    loadContactMessages();
  }, [section, loadContactMessages]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const closeIfDesktop = () => {
      if (mq.matches) setMobileNavOpen(false);
    };
    closeIfDesktop();
    mq.addEventListener('change', closeIfDesktop);
    return () => mq.removeEventListener('change', closeIfDesktop);
  }, []);

  return (
    <Box className="admin-shell">
      <div className="admin-mobile-wrap">
        <header className="admin-mobile-topbar">
          <span className="admin-mobile-topbar__title">Admin panel</span>
          <IconButton
            type="button"
            color="inherit"
            onClick={() => setMobileNavOpen((open) => !open)}
            aria-expanded={mobileNavOpen}
            aria-controls="admin-mobile-menu"
            aria-label={mobileNavOpen ? 'Close admin menu' : 'Open admin menu'}
            size="medium"
            className="admin-mobile-topbar__toggle"
          >
            <Menu />
          </IconButton>
        </header>
        {mobileNavOpen && (
          <>
            <button
              type="button"
              className="admin-mobile-backdrop"
              aria-label="Close menu"
              onClick={() => setMobileNavOpen(false)}
            />
            <nav
              id="admin-mobile-menu"
              className="admin-mobile-menu"
              aria-label="Admin navigation"
            >
              {ADMIN_NAV_ITEMS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`admin-mobile-menu__item${
                    section === id ? ' admin-mobile-menu__item--active' : ''
                  }`}
                  onClick={() => {
                    setSection(id);
                    setMobileNavOpen(false);
                  }}
                >
                  <Icon className="admin-mobile-menu__icon" fontSize="small" />
                  {label}
                </button>
              ))}
              <button
                type="button"
                className="admin-mobile-menu__item admin-mobile-menu__item--logout"
                onClick={() => {
                  setMobileNavOpen(false);
                  onLogout();
                }}
              >
                <Logout className="admin-mobile-menu__icon" fontSize="small" />
                Sign out
              </button>
            </nav>
          </>
        )}
      </div>

      <aside className="admin-sidebar" aria-label="Admin navigation">
        <div className="admin-sidebar-brand">
          Tourasco Hotels
          <small>Admin panel</small>
        </div>
        <nav className="admin-sidebar-nav">
          {ADMIN_NAV_ITEMS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              className={`admin-sidebar-nav-btn admin-sidebar-link${
                section === id ? ' admin-sidebar-link--active' : ''
              }`}
              onClick={() => setSection(id)}
            >
              <Icon fontSize="small" />
              {label}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <Button
            type="button"
            variant="contained"
            onClick={onLogout}
            fullWidth
            startIcon={<Logout />}
            sx={{
              textTransform: 'none',
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { background: 'rgba(255,255,255,0.3)', boxShadow: 'none' },
            }}
          >
            Sign out
          </Button>
        </div>
      </aside>

      <main className="admin-main">
        {section === 'dashboard' && (
          <article className="admin-welcome-card">
            <Typography component="h1" className="admin-welcome-title">
              Welcome back
            </Typography>
            <Typography className="admin-welcome-lede">
              You are signed in to the administrator dashboard. Use the sidebar for users, bookings,
              and messages sent from the Contact page.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Signed in as:
            </Typography>
            <div className="admin-welcome-meta">
              <span className="admin-welcome-chip">{displayEmail}</span>
            </div>
          </article>
        )}

        {section === 'users' && (
          <article className="admin-welcome-card admin-users-card">
            <Typography component="h1" className="admin-welcome-title">
              Users
            </Typography>
            <Typography className="admin-welcome-lede" sx={{ mb: 2 }}>
              Accounts stored in the database.
            </Typography>

            {usersError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {usersError}
              </Alert>
            )}

            {usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} className="admin-users-table-wrap">
                <Table size="small" stickyHeader aria-label="Registered users">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Registered</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No registered users yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                          </TableCell>
                          <TableCell>{u.phone || '—'}</TableCell>
                          <TableCell>{formatDateTime(u.createdAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </article>
        )}

        {section === 'bookings' && (
          <article className="admin-welcome-card admin-users-card">
            <Typography component="h1" className="admin-welcome-title">
              Bookings
            </Typography>
            <Typography className="admin-welcome-lede" sx={{ mb: 2 }}>
              All hotel bookings saved by signed-in customers.
            </Typography>

            {bookingsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bookingsError}
              </Alert>
            )}

            {bookingsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} className="admin-users-table-wrap">
                <Table size="small" stickyHeader aria-label="All bookings">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Hotel</TableCell>
                      <TableCell>Check-in</TableCell>
                      <TableCell>Check-out</TableCell>
                      <TableCell>Guest</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Booked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No bookings yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      bookings.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{b.accountEmail || '—'}</TableCell>
                          <TableCell>{b.hotelName || '—'}</TableCell>
                          <TableCell>{formatDateOnly(b.checkIn)}</TableCell>
                          <TableCell>{formatDateOnly(b.checkOut)}</TableCell>
                          <TableCell>{guestLabel(b)}</TableCell>
                          <TableCell>{formatMoney(b.totalPrice, b.currency)}</TableCell>
                          <TableCell>{b.status || '—'}</TableCell>
                          <TableCell>{b.confirmationRef || '—'}</TableCell>
                          <TableCell>{formatDateTime(b.createdAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </article>
        )}

        {section === 'contact' && (
          <article className="admin-welcome-card admin-users-card">
            <Typography component="h1" className="admin-welcome-title">
              Contact
            </Typography>
            <Typography className="admin-welcome-lede" sx={{ mb: 2 }}>
              Requests from the &quot;Send us a Message&quot; form on the Contact page.
            </Typography>

            {contactError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {contactError}
              </Alert>
            )}

            {contactLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} className="admin-users-table-wrap">
                <Table size="small" stickyHeader aria-label="Contact form messages">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Message</TableCell>
                      <TableCell>Submitted</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contactMessages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No contact messages yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      contactMessages.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>{m.name || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.email || '—'}</TableCell>
                          <TableCell>{m.phone || '—'}</TableCell>
                          <TableCell>{m.subject || '—'}</TableCell>
                          <TableCell
                            title={m.message || ''}
                            sx={{
                              maxWidth: 320,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {truncateText(m.message, 200)}
                          </TableCell>
                          <TableCell>{formatDateTime(m.createdAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </article>
        )}
      </main>
    </Box>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [sessionEmail, setSessionEmail] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdminSession()) {
      setAuthed(true);
      setSessionEmail(getSessionAdminEmail());
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    sessionStorage.removeItem(ADMIN_SESSION_EMAIL_KEY);
    sessionStorage.removeItem(ADMIN_RPC_SESSION_PASSWORD_KEY);
    setAuthed(false);
    setSessionEmail('');
    setPassword('');
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim() || !password) {
      setError('Enter email and password.');
      setLoading(false);
      return;
    }

    const normalizedInput = normalizeEmail(email);
    const normalizedAdmin = normalizeEmail(ADMIN_PANEL_EMAIL);
    const ok = normalizedInput === normalizedAdmin && password === ADMIN_PANEL_PASSWORD;

    if (ok) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, '1');
      sessionStorage.setItem(ADMIN_SESSION_EMAIL_KEY, normalizedInput);
      try {
        sessionStorage.setItem(ADMIN_RPC_SESSION_PASSWORD_KEY, password);
      } catch {
        /* storage blocked */
      }
      setSessionEmail(normalizedInput);
      setAuthed(true);
      setPassword('');
    } else {
      setError('Invalid email or password.');
    }
    setLoading(false);
  };

  if (authed) {
    return <AdminDashboard adminEmail={sessionEmail} onLogout={handleLogout} />;
  }

  return (
    <Box className="admin-page">
      <Container maxWidth="sm">
        <Box className="admin-container">
          <Paper elevation={3} className="admin-paper">
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <AdminPanelSettings className="admin-brand-icon" sx={{ fontSize: 48 }} />
            </Box>
            <Typography variant="h4" className="admin-title">
              Admin sign in
            </Typography>
            <Typography variant="body2" className="admin-subtitle">
              Sign in with the administrator email and password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="admin-form" noValidate>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                className="admin-submit-btn"
                sx={{ mt: 2, py: 1.5 }}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
              <Box className="admin-back-wrap">
                <Typography variant="body2" className="admin-back-text">
                  <Link to="/" className="admin-back-link">
                    ← Back to website
                  </Link>
                </Typography>
              </Box>
            </form>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Admin;
